import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { StoredError, ErrorStore } from "./types";
import { v4 as uuid } from "uuid";

export const useErrorStore = create<ErrorStore>()(
  persist(
    (set, get) => ({
      errors: [],

      addError: (error) => {
        const message =
          error instanceof Error ? error.message : "Unknown error";
        // Prevent duplicate errors for the same endpoint within a short time
        const existingError = get().errors.find(
          (error) =>
            error.message === message &&
            !error.resolved &&
            Date.now() - new Date(error.timestamp).getTime() < 5000, // 5 seconds
        );

        if (existingError) {
          // Update retry count instead of adding new error
          set((state) => ({
            errors: state.errors.map((error) =>
              error.id === existingError.id
                ? { ...error, retryCount: error.retryCount + 1 }
                : error,
            ),
          }));
        } else {
          const error: StoredError = {
            message,
            id: uuid(),
            timestamp: new Date().toISOString(),
            retryCount: 0,
            resolved: false,
          };

          set((state) => ({
            errors: [...state.errors.slice(-9), error], // Keep only last 10 errors
          }));
        }
      },

      resolveError: (id) => {
        set((state) => ({
          errors: state.errors.map((error) =>
            error.id === id ? { ...error, resolved: true } : error,
          ),
        }));
      },

      getUnresolvedErrors: () => {
        return get().errors.filter((error) => !error.resolved);
      },

      clearErrors: () => {
        set({ errors: [] });
      },
    }),
    {
      name: "api-error-store",
      partialize: (state) => ({
        errors: state.errors.filter((error) => !error.resolved).slice(-5), // Only persist last 5 unresolved errors
      }),
    },
  ),
);
